/**
 * Admin-action review queue — ADMIN track only.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  OWNER DECISION 2026-05-18 — owner away (Kailash, unreachable ~26 May →
 *  08 Jun): HIGH-risk WhatsApp admin commands (money / delete / bulk) are
 *  NOT actioned over WhatsApp while away. They persist as a DURABLE queue
 *  the owner reviews HERE, in one batch, ON RETURN.
 *
 *  This screen is the owner's batch-review surface:
 *    - lists the queued HIGH-risk actions (newest first, queued on top)
 *    - per row: a plain-language summary + Approve / Reject buttons
 *  Approve → the server re-checks admin role + re-derives the risk tier +
 *  runs the conservative executor (records the proposed change / soft-
 *  delete only — never a blind ledger move). Reject → nothing executes.
 *
 *  Defence in depth: the route is admin-gated (RequireAuth roles={['admin']})
 *  AND this screen re-asserts the admin track. The backing collection is
 *  server-only (firestore.rules) — all reads/decisions go through the
 *  admin-guarded callables. Clients/staff NEVER see this (visibility model).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { Box, Btn3, EmptyNote, Tag } from '../components/ui';
import {
  decideAdminAction,
  describeAction,
  fetchAdminActionQueue,
  isPending,
  statusLabel,
  type QueueItem,
} from '../lib/adminActionQueue';

function fmtWhen(ms: number | null): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '';
  }
}

export default function AdminActionQueue() {
  const { role } = useAuth();
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setItems(await fetchAdminActionQueue());
    } catch {
      setErr('Could not load the review queue. Try again.');
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Defence-in-depth: admin track only (route is also admin-gated).
  if (role !== 'admin') {
    return (
      <Box title="Admin action review" note="Restricted">
        <EmptyNote title="Not available on this track">
          The admin-action review queue is admin-only.
        </EmptyNote>
      </Box>
    );
  }

  async function decide(
    id: string,
    decision: 'approve' | 'reject',
  ): Promise<void> {
    setBusyId(id);
    setErr(null);
    try {
      let rejectReason: string | undefined;
      if (decision === 'reject') {
        const r = window.prompt('Reason for rejecting (optional):') || '';
        rejectReason = r.trim() || undefined;
      }
      await decideAdminAction(id, decision, rejectReason);
      await load();
    } catch {
      setErr(
        decision === 'approve'
          ? 'Approve failed — the item may already be decided. Refreshed.'
          : 'Reject failed — refreshed.',
      );
      await load();
    } finally {
      setBusyId(null);
    }
  }

  const list = items || [];
  const pending = list.filter((i) => isPending(i.status));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Box
        title="Admin action review — WhatsApp HIGH-risk queue"
        note={
          items === null
            ? 'Loading…'
            : `${pending.length} awaiting your review · money/deletes are NOT actioned over WhatsApp while you are away`
        }
      >
        {err && (
          <div style={{ marginBottom: 12 }}>
            <Tag kind="warn">{err}</Tag>
          </div>
        )}

        {items !== null && list.length === 0 && (
          <EmptyNote title="Nothing queued">
            No HIGH-risk admin commands are waiting. Anything sensitive sent
            over WhatsApp while you were away will appear here for your
            approval.
          </EmptyNote>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map((it) => {
            const decidable = isPending(it.status);
            const busy = busyId === it.id;
            return (
              <div
                key={it.id}
                className="row-card"
                style={{
                  border: '1px solid var(--line, #e5e5e5)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  background: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <Tag kind={decidable ? 'warn' : 'default'}>
                    {statusLabel(it.status)}
                  </Tag>
                  <strong style={{ fontSize: 14 }}>
                    {describeAction(it)}
                  </strong>
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>
                  From {it.from || 'admin'}
                  {it.createdAt ? ` · ${fmtWhen(it.createdAt)}` : ''}
                  {it.receipt ? ` · ${it.receipt}` : ''}
                  {it.rejectReason ? ` · reason: ${it.rejectReason}` : ''}
                </div>
                {decidable && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Btn3
                      intent="grn"
                      size="sm"
                      disabled={busy}
                      onClick={() => void decide(it.id, 'approve')}
                    >
                      {busy ? 'Working…' : 'Approve & run'}
                    </Btn3>
                    <Btn3
                      intent="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void decide(it.id, 'reject')}
                    >
                      Reject
                    </Btn3>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Box>
    </div>
  );
}
